import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { X, Zap, CheckCircle, Lightbulb, Play } from "lucide-react";

/**
 * FeatureInfoModal - Displays detailed feature information
 * 
 * SAFETY: This component is purely additive and display-only.
 * - Reads optional metadata from feature.metadata
 * - Does NOT modify any data
 * - Does NOT affect billing, entitlements, or purchase logic
 * - Falls back gracefully if metadata is missing
 */
export function FeatureInfoModal({ 
  isOpen, 
  onClose, 
  feature, 
  onEnableFeature,
  isOwned = false,
  isIncludedInBundle = false,
  isSelected = false
}) {
  if (!feature) return null;

  // Safe access to optional metadata
  const metadata = feature.metadata || {};
  const tagline = metadata.tagline;
  const whatItDoes = metadata.what_it_does;
  const whyItMatters = metadata.why_it_matters;
  const exampleWorkflow = metadata.example_workflow;

  // Check if we have any metadata to display
  const hasMetadata = tagline || whatItDoes?.length || whyItMatters?.length || exampleWorkflow;

  const handleEnableClick = () => {
    if (onEnableFeature && !isOwned && !isIncludedInBundle) {
      onEnableFeature();
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[640px] p-0 overflow-hidden max-h-[90vh] flex flex-col"
        data-testid="feature-info-modal"
      >
        {/* Header */}
        <DialogHeader className="p-8 pb-4 border-b border-zinc-100">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <DialogTitle className="text-2xl font-bold text-zinc-900">
                {feature.name}
              </DialogTitle>
              {tagline && (
                <p className="text-base text-zinc-600 leading-relaxed">
                  {tagline}
                </p>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-6">
          {/* Feature Description (always show) */}
          {feature.description && !hasMetadata && (
            <div className="space-y-3">
              <p className="text-zinc-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          )}

          {/* What It Does Section */}
          {whatItDoes && whatItDoes.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                <Zap size={14} className="text-[#FF6600]" />
                What It Does
              </h3>
              <ul className="space-y-2">
                {whatItDoes.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-zinc-700 leading-relaxed">
                    <CheckCircle size={16} className="text-emerald-500 mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Why It Matters Section */}
          {whyItMatters && whyItMatters.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                <Lightbulb size={14} className="text-amber-500" />
                Why It Matters
              </h3>
              <ul className="space-y-2">
                {whyItMatters.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-zinc-700 leading-relaxed">
                    <CheckCircle size={16} className="text-emerald-500 mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Example Workflow Section */}
          {exampleWorkflow && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                <Play size={14} className="text-blue-500" />
                Example Workflow
              </h3>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap font-mono">
                  {exampleWorkflow}
                </p>
              </div>
            </div>
          )}

          {/* Fallback if no metadata */}
          {!hasMetadata && feature.description && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                <Zap size={14} className="text-[#FF6600]" />
                About This Feature
              </h3>
              <p className="text-zinc-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-zinc-100 bg-zinc-50">
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="px-4 py-2"
            >
              Close
            </Button>
            <Button
              onClick={handleEnableClick}
              disabled={isOwned || isIncludedInBundle}
              className={
                isOwned 
                  ? "bg-zinc-200 text-zinc-500 cursor-not-allowed px-5 py-2" 
                  : isIncludedInBundle 
                    ? "bg-emerald-100 text-emerald-700 cursor-not-allowed px-5 py-2" 
                    : "bg-[#FF6600] hover:bg-[#CC5200] text-white px-5 py-2"
              }
              data-testid="enable-feature-btn"
            >
              {isOwned 
                ? "Already Owned" 
                : isIncludedInBundle 
                  ? "Included in Bundle" 
                  : isSelected
                    ? "Selected ✓"
                    : "Enable Feature"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FeatureInfoModal;
